import axios from "axios";
import {
  AmbientLight,
  Box3,
  Camera,
  Color,
  CylinderGeometry,
  DirectionalLight,
  Euler,
  LinearSRGBColorSpace,
  Matrix4,
  Mesh,
  Object3D,
  OrthographicCamera,
  Quaternion,
  Raycaster,
  Scene,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three";
import { Common } from "../../common/src";
import { RtkFixData } from "../../common/src/modules";
import { CameraManager } from "./camera/CameraManager";
import { ControlManager } from "./camera/controls/ControlManager";
import { DxfManager } from "./dxf/DxfManager";
import { IQuick3DCamera } from "./camera/quick3d-camera-interface";
import {
  IOrthographicFitParameters,
  Quick3DOrthographicCamera,
} from "./camera/quick3d-orthographic-camera";
import {
  CanvasPolygonSelection,
  ISelectionData,
} from "./Canvas/polygon-selection";
import {
  DEFAULT_POINT_SIZE,
  ITextScale,
  SCENE_TYPE,
  setViewType,
} from "./config/ViewerConfig";
import {
  MeasurementCommandHistory,
  SetMeasurementPointsCommand,
} from "./measurement/command/measurementCommandHistory";
import { CoordTracker } from "./measurement/CoordTracker";
import { MeasurementManager } from "./measurement/MeasurementManager";
import { MeasurementSelector } from "./measurement/MeasurementSelector";
import { PanelDragObservable } from "./measurement/PanelDragObservable";
import {
  IEditMeasurementParams,
  IMeasurement,
  IMeasurementHandler,
  IMeasurementVisibleParams,
  IPanelDraggable,
  MeasurementType,
  TCreateMeasurementParams,
  TMeasurementEditObserver,
} from "./measurement/types";
import { VolumeCalculationMeasurement } from "./measurement/VolumeCalculationMeasurement";
import { ObjectManager } from "./object/ObjectManager";
import { Photoshot } from "./object/Photoshot";
import { PhotoshotManager } from "./object/PhotoshotManager";
import { PreviewManager } from "./object/PreviewManager";
import { IPointPickerCallback, PointPicker } from "./picker/PointPicker";
import { IVolumeInfo } from "./pointcloud/HeightMapHandler";
import { IPointCloudLoadParams } from "./pointcloud/loader/interface";
import { PointCloudManager } from "./pointcloud/PointCloudManager";
import { IPointColorRangeInfo } from "./pointcloud/PointCloudTile";
import { AxisUI } from "./ui/AxisUI";
import { TControlMode, TProjectionMode } from "./utils/camera-utils";
import { Observable, observe } from "../utils/observe";
import { DesignManager, DesignSource, TSavedIfcObject } from "./design/DesignManager";
import {
  createInstanceStore,
  InstanceStore,
} from "./editor/instanceStore";
import { InputHandler } from "./utils/input-handler/input-handler";
import * as Utils from "./utils/utils";
import { vec3 } from "./packages/math";

// import Stats from "stats.js"; // for debug purpose

/** Allowed fixed FPS caps for the render loop */
type TTargetFPS = 15 | 30 | 45 | 60;

// IFC 配置オブジェクトの多方向ビュー指定
export type TDesignObjectView =
  | "top"
  | "bottom"
  | "front"
  | "back"
  | "left"
  | "right";

@Observable
export class Viewer {
  private _canvas: HTMLCanvasElement;

  private _sceneMap: Map<SCENE_TYPE, Scene>;
  private _renderer: WebGLRenderer;

  // camera
  private _cameraManager: CameraManager;
  private _worldSpace: Matrix4;
  private _worldSpaceInverse: Matrix4;
  private _worldQuaternion: Quaternion;
  private _controlManager: ControlManager;
  private _currentSelection: CanvasPolygonSelection;
  private _poligonIndicesCount: number = 0;
  private _poligonMaxCount: number = 0;
  
  public cloudPointOffset = [0, 0, 0] as vec3;

  // ── SCM lane (new) — independent of the old design road below.
  /** Presigned URL of the SCM design. Assign to load, set null to unload. */
  public scmURL: string | null = null;
  public scmStatus = "blank" as "blank" | "loading" | "loaded" | "error";
  public scmVisible: boolean = true;

  // ── Old design road (IFC only) — same API as before; ignores .scm URLs.
  public designURL: string | null = null;
  public designVisible: boolean = true;
  public designStatus = "blank" as "blank" | "loading" | "loaded" | "error";
  public designSource: { url: string; format: "ifc" | "gltf" | "fbx" } | null = null;
  
  public designOffsetMode = "cloudpoint" as "cloudpoint" | "bound";
  public designBoundOffset = [0, 0, 0] as vec3;

  public gsplatStatus = "blank" as "blank" | "loading" | "loaded" | "error";

  public designManager = new DesignManager(this);

   // DXF state
  public dxfURL: string | null = null;
  public dxfStatus = "blank" as "blank" | "loading" | "loaded" | "error";
  public dxfBlendEnabled: boolean = false;
  public dxfBlendRadius: number = 2;
  public dxfBlendHardness: number = 0;
  public dxfBlendOpacity: number = 1;

  public dxfManager = new DxfManager(this);

  // The kho editor: the Viewer OWNS the design scene + instance store (was
  // created inside DesignManager — moved here as DesignManager is dissolved).
  // The editor-facing wrappers below route straight to this store; the gizmo /
  // instance / viewer-adapter systems run inside it.
  private _designScene: Scene | null = null;
  private _instances: InstanceStore | null = null;
  // The AR placeholder is a REAL, selected provisional instance in the editor
  // (not a translucent ghost). This tracks its id for the placeholder wrappers.
  private _placeholderInstanceId: string | null = null;
  // committedId (DB id) → instanceId, so saved objects reconcile across reloads
  // instead of duplicating (renderSavedIfcObjects keys off this).
  private _committedToInstance = new Map<string, string>();

  /** The design scene the editor renders into (shared with the legacy terrain). */
  public getDesignScene(): Scene {
    if (!this._designScene) {
      this._designScene = new Scene();
      this._designScene.add(new DirectionalLight(0xffffff, 1).translateY(-1));
      const l2 = new DirectionalLight(0xffffff, 1);
      l2.position.set(1, 1, 1);
      this._designScene.add(l2);
      this._designScene.add(new AmbientLight(0xffffff, 2));
    }
    return this._designScene;
  }

  /** The kho editor instance store (lazily created, Viewer-owned). */
  public getInstanceStore(): InstanceStore {
    if (!this._instances) {
      this._instances = createInstanceStore(this.getDesignScene(), {
        getCamera: () => this.getCamera() as unknown as Camera,
        getRenderer: () => this.getRenderer(),
        getCloudScenes: () => this._gatherCloudScenes(),
        // Adaptive near/far comes from the design-terrain feature (DesignManager);
        // AR instances sit on that terrain and share its clip envelope.
        getDesignClip: (camera) => this.designManager.getDesignClip(camera),
        setDragActive: (active) => {
          this.designDragActive = active;
        },
      });
    }
    return this._instances;
  }

  /** Object3D of the editor instance `id`, or null (mesh may still be loading). */
  private _instanceMeshById(id: string | null): Object3D | null {
    if (!id) return null;
    const grp = this.getInstanceStore().getGroup();
    return grp?.children.find((o) => o.userData.instanceId === id) ?? null;
  }

  /** Object3D of the currently-selected editor instance, or null. */
  private _selectedInstanceMesh(): Object3D | null {
    return this._instanceMeshById(this.getInstanceStore().getSelection());
  }

  public designDragActive = false;
  public set poligonIndicesCount(v: number) {
    this._poligonIndicesCount = v;
  }

  public get poligonIndicesCount() {
    return this._poligonIndicesCount;
  }

  public set poligonMaxCount(v: number) {
    this._poligonMaxCount = v;
  }

  public get poligonMaxCount() {
    return this._poligonMaxCount;
  }

  private _objectManager: ObjectManager;
  private _previewManager: PreviewManager;
  private _measurementManager: MeasurementManager;
  public photoshotManager: PhotoshotManager;
  public _pointCloudManager: PointCloudManager;
  private _pointPicker: PointPicker;
  private _inputHandler: InputHandler;
  // private _stats: Stats;
  // private _pointCloudSettings: PointCloudSettings;
  private _abortController: AbortController;

  private _axisUI: AxisUI;

  private _measurementCommandHistory: MeasurementCommandHistory;

  private _renderLoopCount: number = 2;
  public debouncedOnCameraChange: (target: number) => void;
  private _isInit: boolean;
  private _loadId: number = 0;
  private _currentType: Common.POINT_CLOUD_TYPE;

  public testUIVal: number[];

  private _pointSize: number;
  private _measurementSelector: MeasurementSelector;
  private _panelDragObservable: PanelDragObservable;

  public whileOperation: boolean;

  public getPointSize() {
    return this._pointSize;
  }
  public viewType = null as string;

  private _globalSelectedCount: number = 0;
  private _maxSelectionLimit: number = 100;


  // Fixed FPS.
  private _targetFPS: TTargetFPS = 30;
  private _lastFrameTime: number = 0;
  // Guard: the render loop must be started exactly once
  private _renderLoopStarted: boolean = false;

  public get targetFPS(): TTargetFPS {
    return this._targetFPS;
  }
  public setTargetFPS(fps: TTargetFPS): void {
    this._targetFPS = fps;
  }
  // 点群のサイズを変更
  public changePointSize(size: number): void {
    this._pointSize = size;
    this._pointCloudManager.setPointSize(size);
  }

  public get loadId(): number {
    return this._loadId;
  }

  public getCurrentType(): Common.POINT_CLOUD_TYPE {
    return this._currentType;
  }

  public setCurrentType(v: Common.POINT_CLOUD_TYPE) {
    this._currentType = v;
  }

  public getActiveSelectionInfo() {
    return this._pointCloudManager.activeSelectionInfo;
  }

  public get pointMaterial() {
    return this._pointCloudManager.pointMaterial;
  }

  public changeTestUIVal(index: number, val: number) {
    this.testUIVal[index] = val;
  }

  public getTestUIVal(index: number) {
    return this.testUIVal[index];
  }

  constructor() {
    this.testUIVal = [0.5, 1.3, 0.1, 10.0, 1.0];

    this._pointSize = DEFAULT_POINT_SIZE;

    // this._pointCloudSettings = settings;
    this._sceneMap = new Map();
    this._sceneMap.set(SCENE_TYPE.BACKGROUND, new Scene());
    this._sceneMap.set(SCENE_TYPE.STREAM_POINTCLOUD, new Scene());
    this._sceneMap.set(SCENE_TYPE.STATIC_POINTCLOUD, new Scene());
    this._sceneMap.set(SCENE_TYPE.MEASUREMENT, new Scene());
    this._sceneMap.set(SCENE_TYPE.POINTPICK, new Scene());
    this._sceneMap.set(SCENE_TYPE.FOREGROUND, new Scene());
    this._objectManager = new ObjectManager(
      this._sceneMap.get(SCENE_TYPE.BACKGROUND),
      this
    );

    this._abortController = new AbortController();
    this._measurementCommandHistory = new MeasurementCommandHistory();

    this.debouncedOnCameraChange = Utils.debounce(() => {
      // 回転終了
      this._cameraManager.rotateEnd();
      this._onCameraChange();
    }, 300);
    this.photoshotManager = new PhotoshotManager();

    (window as any)["viewer"] = this;
  }

  public getCanvas(): HTMLCanvasElement {
    return this._canvas;
  }

  public Resize(): void {
    if (!this._canvas) {
      return;
    }
    this._canvas.style.height = "100%";
    this._canvas.style.width = "100%";
    this._renderer.setPixelRatio(window.devicePixelRatio);
    this._renderer.setSize(this._canvas.clientWidth, this._canvas.clientHeight);

    this._cameraManager.setAspect(
      this._canvas.clientWidth / this._canvas.clientHeight
    );
    this._axisUI.resize();
    if (this._currentSelection) {
      this._currentSelection.copyMainCanvasSize(this._canvas);
    }
  }

  private SetCamera(bbox: Box3): void {
    this._cameraManager.camera.updateProjectionMatrix();
  }

  private InitRenderer(canvas: HTMLCanvasElement): void {
    this._canvas = canvas;
    // keep buffer Data
    let contextAttributes = {
      alpha: true,
      depth: true,
      stencil: false,
      antialias: false,
      preserveDrawingBuffer: true,
      powerPreference: "high-performance",
    } as WebGLContextAttributes;
    const gl = this._canvas.getContext(
      "webgl2",
      contextAttributes
    ) as WebGLRenderingContext;

    this._renderer = new WebGLRenderer({
      alpha: true,
      premultipliedAlpha: false,
      canvas: canvas,
      preserveDrawingBuffer: true,
    });
    // this._renderer.toneMapping = NoToneMapping;
    this._renderer.outputColorSpace = LinearSRGBColorSpace;
    this._renderer.autoClear = false;
    this._renderer.localClippingEnabled = true;
    this._renderer.setPixelRatio(window.devicePixelRatio);
    this._renderer.setSize(this._canvas.clientWidth, this._canvas.clientHeight);
  }

  private InitCamera() {
    // camera
    const worldX = new Vector3(1, 0, 0);
    const worldY = new Vector3(0, 0, 1);
    const worldZ = new Vector3(0, -1, 0);
    this._worldSpace = new Matrix4().makeBasis(worldX, worldY, worldZ);
    this._worldSpaceInverse = new Matrix4().copy(this._worldSpace).invert();
    this._worldQuaternion = new Quaternion().setFromRotationMatrix(
      this._worldSpace
    );
    this._cameraManager = new CameraManager(
      this._worldQuaternion,
      this._inputHandler,
      this
    );
    const dirLight = new DirectionalLight(0xffffff, 2);
    dirLight.position.set(0, 0, 2000);
    dirLight.lookAt(0, 0, -1);
    this._cameraManager.targetObject.add(dirLight);
  }

  public enableControl(controls: {
    pan?: boolean;
    rotate?: boolean;
    dolly?: boolean;
  }) {
    const { pan, rotate, dolly } = controls;
    this._cameraManager.enableControl({ pan, rotate, dolly });
  }

  private InitEventListners(): void {
    // bind
    this.Resize = this.Resize.bind(this);

    document.addEventListener("startController", () => {
      this.whileOperation = true;
      // console.log("startController");
    });

    document.addEventListener("endController", () => {
      this.whileOperation = false;
      // console.log("endController");
    });

    // this._controller.addEventListener("start", () => {
    // console.log("start");
    // this.togglePickPointGuide(false);
    // });
    // this._controller.addEventListener("change", () => {
    //   // console.log("change");

    //   // this._flgRender = true;
    //   this.updateRenderer(1);
    // });
    // this._controller.addEventListener("end", () => {
    //   // this._pointCloudManager.onCameraChange(
    //   //   this._cameraManager.camera,
    //   //   new AbortController()
    //   // );
    //   // console.log("end");
    //   // this._onCameraChange();
    //   this.debouncedOnCameraChange(1000);
    //   // this._flgRender = true;
    //   this.updateRenderer();
    // });
    const ua = navigator.userAgent;
    if (
      ua.indexOf("Windows") > 0 ||
      (ua.indexOf("Macintosh") && !("ontouchend" in document))
    ) {
      setTimeout(() => {
        window.addEventListener("resize", this.Resize);
        this.Resize();
      }, 1000);
    }
  }

  singleMeasurementRender(): void {
    this._objectManager.animateText(this._cameraManager.camera);

    this._renderer.render(
      this._sceneMap.get(SCENE_TYPE.MEASUREMENT),
      this._cameraManager.camera
    );
  }

  singleRender(): void {
    this._objectManager.animateText(this._cameraManager.camera);

    this._renderer.clear();

    this._renderer.render(
      this._sceneMap.get(SCENE_TYPE.BACKGROUND),
      this._cameraManager.camera
    );

    this._renderer.render(
      this._sceneMap.get(SCENE_TYPE.STATIC_POINTCLOUD),
      this._cameraManager.camera
    );

    this._renderer.render(
      this._sceneMap.get(SCENE_TYPE.STREAM_POINTCLOUD),
      this._cameraManager.camera
    );

    // this._renderer.render(
    //   this._sceneMap.get(SCENE_TYPE.MEASUREMENT),
    //   this._cameraManager.camera
    // );

    // this._renderer.render(
    //   this._sceneMap.get(SCENE_TYPE.POINTPICK),
    //   this._cameraManager.camera
    // );

    this._axisUI.render();

    this._lastFrameTime = 0;
  }

  /**
   * DO NOT CALL THIS. The render loop starts itself once from `Init()`.
   *
   * The loop redraws the whole scene every frame at the capped FPS, so nothing
   * needs to request a render: just update the data (camera, scene objects,
   * uniforms) and the next frame shows it.
   *
   * Kept `public` on purpose so older call sites still compile — every call
   * after the first one is a no-op.
   */
  public MainRender(): void {
    if (this._renderLoopStarted) {
      return;
    }
    this._renderLoopStarted = true;
    this._renderLoop();
  }

  /** The actual rAF loop — the only place calling requestAnimationFrame. */
  private _renderLoop = (): void => {
    requestAnimationFrame(this._renderLoop);

    // Fixed FPS: only draw once 1000/_targetFPS ms have passed since the last frame.
    const frameInterval = 1000 / this._targetFPS;
    const now = performance.now();
    const elapsed = now - this._lastFrameTime;
    if (elapsed < frameInterval) {
      return;
    }
    // Subtract the remainder so drift does not accumulate across frames
    this._lastFrameTime = now - (elapsed % frameInterval);

    this._renderer.clear();

    // this._objectManager.animateText(this._cameraManager.camera);

    this._renderer.render(
      this._sceneMap.get(SCENE_TYPE.BACKGROUND),
      this._cameraManager.camera
    );

      // point cloud (static + stream)
    this._renderer.render(
      this._sceneMap.get(SCENE_TYPE.STATIC_POINTCLOUD),
      this._cameraManager.camera
    );
    this._renderer.render(
      this._sceneMap.get(SCENE_TYPE.STREAM_POINTCLOUD),
      this._cameraManager.camera
    );

      // Render design scene: the DesignManager terrain (its own scene) first,
      // then the kho editor AR instances + gizmo (Viewer-owned design scene) on
      // top so placed objects sit above the terrain they rest on.
      this.designManager.render(this._renderer, this._cameraManager.camera);
      this.getInstanceStore().renderDesign(
        this._renderer,
        this._cameraManager.camera,
      );

    this._renderer.render(
      this._sceneMap.get(SCENE_TYPE.MEASUREMENT),
      this._cameraManager.camera
    );

    this._renderer.render(
      this._sceneMap.get(SCENE_TYPE.POINTPICK),
      this._cameraManager.camera
    );

    this._axisUI.render();

      this._renderer.render(
        this.photoshotManager.scene,
        this._cameraManager.camera
      );
    
    // this._stats.end();
  };

  public Init(canvas: HTMLCanvasElement): void {
    // canvas.parentElement.appendChild(this._stats.dom);
    // set renderer
    this.InitRenderer(canvas);
    this.InitCamera();
    // init event
    this.InitEventListners();

    this._axisUI = new AxisUI(this._cameraManager.camera, this._renderer);
    this._axisUI.flipXY(true);

    this._objectManager.setCanvas(this._canvas);
    this._objectManager.InitPickPointsGuide();

    this._inputHandler = new InputHandler(this, this._canvas.parentElement);
    this._pointCloudManager = new PointCloudManager(
      this,
      // this._pointCloudSettings,
      { threshold: 0.001 }
    );
    this._previewManager = new PreviewManager(
      this._sceneMap.get(SCENE_TYPE.MEASUREMENT),
      this
    );
    this._pointPicker = new PointPicker(this, this._inputHandler);
    this._measurementManager = new MeasurementManager(
      this,
      this._previewManager,
      this._inputHandler
    );
    // Not registered by default — panels become draggable only after
    // startPanelDrag() (opt-in "move panel" mode, SC_QUICK-2187)
    this._panelDragObservable = new PanelDragObservable(
      this,
      this._measurementManager
    );
    this._measurementSelector = new MeasurementSelector(
      this,
      this._canvas,
      this._measurementManager,
      this._panelDragObservable
    );

    // controller
    this._controlManager = new ControlManager(
      this,
      this._cameraManager,
      this._inputHandler
    );
    this._inputHandler.setControlManager(this._controlManager);

    this._cameraManager.camera.position.set(-100, -100, 100);
    this._cameraManager.camera.up.set(0, 0, 1);
    this._cameraManager.camera.lookAt(0, 0, 0);
    
     this._pointCloudManager.bvh.onOffsetChanged.push(() => {
      const offset = this.getOffset();
      this.cloudPointOffset = [offset.x, offset.y, offset.z];
      console.log("PointCloud Offset:", this.cloudPointOffset);
    });

    // IMPORTANT: Call Resize() before MainRender() to ensure camera has valid projection matrix
    this.Resize();
    this.MainRender();
    this.photoshotManager.init(canvas, this);

    this.singleRender = this.singleRender.bind(this);

    // DesignManager uses its own independent scene (terrain only).
    this.designManager.init();

    // Editor render kicks: pointerdown/up draw the first/last frame of a gizmo
    // gesture, and pointermove keeps the gizmo hover-highlight tracking the
    // cursor while an object is selected (cheap scene redraw).
    window.addEventListener("pointerdown", () => this.updateRenderer());
    window.addEventListener("pointerup", () => this.updateRenderer());
    window.addEventListener("pointermove", () => {
      if (this._instances?.getSelection()) this.updateRenderer();
    });
     this.dxfManager.init();
    this._setupDxfObservers();
  }

  private _setupDxfObservers() {
    // DXF URL changes → load/unload
    observe(this, ["dxfURL"], async () => {
      if (this.dxfURL) {
        this.dxfStatus = "loading";
        try {
          await this.dxfManager.load(this.dxfURL);
          this.dxfStatus = "loaded";
          // Apply blend if overlay is already enabled
          if (this.dxfBlendEnabled) {
            this._bakeDxfAndApply();
          }
        } catch (e) {
          this.dxfStatus = "error";
          console.error("DXF load failed:", e);
        }
      } else {
        this._disableDxfShader();
        this.dxfManager.unload();
        this.dxfStatus = "blank";
      }
    });

    // DXF blend toggle
    observe(this, ["dxfBlendEnabled"], () => {
      if (this.dxfBlendEnabled) {
        this._bakeDxfAndApply();
      } else {
        this._disableDxfShader();
      }
    });

    // Blend params change → just update uniforms (instant, no re-bake)
    observe(this, ["dxfBlendRadius", "dxfBlendHardness", "dxfBlendOpacity"], () => {
      if (this.dxfBlendEnabled) {
        this._updateDxfUniforms();
      }
    });
  }

  /** Enable the DXF overlay: push offset + params into uniforms (GPU samples the texture). */
  private _bakeDxfAndApply() {
    if (!this.dxfManager.hasTexture()) return;
    const offset = (this._pointCloudManager?.bvh?.offset || new Vector3()).clone();
    this.dxfManager.enable(
      offset, this.dxfBlendRadius, this.dxfBlendHardness, this.dxfBlendOpacity
    );
  }

  /** Params changed (radius/hardness/opacity) → uniform-only update, O(1), instant. */
  private _updateDxfUniforms() {
    this.dxfManager.setParams(this.dxfBlendRadius, this.dxfBlendHardness, this.dxfBlendOpacity);
  }

  /** Disable the overlay. */
  private _disableDxfShader() {
    this.dxfManager.disable();
  }

  public handleUpdate() {
  }

  public getRenderer(): WebGLRenderer {
    return this._renderer;
  }

  public getScene(sceneType: SCENE_TYPE = SCENE_TYPE.BACKGROUND): Scene {
    return this._sceneMap.get(sceneType);
  }

  public setCameraTarget(target: Vector3) {
    this._cameraManager.targetObject.position.copy(target);
  }


  public getCamera(): IQuick3DCamera {
    return this._cameraManager.camera;
  }

  private clearScene() {
    this._sceneMap.forEach((key, value) => {
      if (
        value === SCENE_TYPE.POINTPICK &&
        this._inputHandler.getInputHandler().has("pickingMode")
      ) {
        return;
      } else {
        key.clear();
      }
    });
  }

  public clear() {
    this.clearScene();
    this._pointCloudManager.clear();
    this._pointCloudManager.resetHighlightSelectedPointCloudSection();
    // this._pointCloudManager.clearPointSize();
  }

  public abortLoadPointCloud() {
    this._abortController.abort();
    this._abortController = new AbortController();
  }

  public async loadPointCloud(
    type: string,
    projectId: string,
    timestamp: string,
    params: IPointCloudLoadParams
  ) {
    //clear the existing pointcloud
    this.viewType = type;
    this._loadId++;

    const load = this._loadId;
    this.clear();
    this._isInit = true;
    await this._pointCloudManager.loadPointCloud(
      type,
      projectId,
      timestamp,
      params,
      this._abortController,
      load
    );
    this.SetCamera(this._pointCloudManager.bvh.rootTile.boundingBox);
    this.fitAllCamera();

    // Initialize the point material's camera info with the first fitted view.
    this.pointMaterial.setCameraInfo(
      this.getBoundingBox().getSize(new Vector3()).length(),
      this.getCamera().position
    );
    this._onCameraChange();
    this._isInit = false;
    const event = new CustomEvent("pointloaded", { detail: this._currentType });
    document.dispatchEvent(event);
  }

  private async _onCameraChange() {
    if (!this._isInit) {
      this.abortLoadPointCloud();
    }
    // this._abortController.abort()
    // this._abortController = new AbortController()
    await this._pointCloudManager?.onCameraChange(
      this._cameraManager.camera,
      this._abortController
    );
    // this._flgRender = true;

    // if (tiles && tiles.length > 0) {
    // const measurements = this._measurementManager.getMeasurementByType(
    //   MeasurementType.VOLUME_CALCULATION
    // );
    // if (measurements.length > 0) {
    //   for (const measurement of measurements) {
    //     const m = measurement as IVolumeCalculationMeasurement;
    //     this._pointCloudManager.bvh.rootTile.applyHeightBasedColor(
    //       m.pointColorRangeInfo,
    //       m.polygonMesh,
    //       new Box2().setFromPoints(
    //         m.polygonMesh.vertices as unknown as Vector2[]
    //       ),
    //       this._pointCloudManager.offsetMatrix
    //     );
    //     // for (const tile of tiles) {

    //     //   tile.applyHeightBasedColor(
    //     //     m.pointColorRangeInfo,
    //     //     m.polygonMesh,
    //     //     new Box2().setFromPoints(
    //     //       m.polygonMesh.vertices as unknown as Vector2[]
    //     //     ),
    //     //     this._pointCloudManager.offsetMatrix,
    //     //     false
    //     //   );
    //     // }
    //   }
    // }
    // }
  }

  public loadTrajectory(
    url: string,
    abortController: AbortController
  ): Promise<void> {
    return new Promise(async (resolve, reject) => {
      await this._objectManager
        .loadTrajectory(url, abortController)
        .then(() => resolve())
        .catch(() => reject());
    });
  }

  public unloadPhotoshots(): void {
    this.photoshotManager.clear();
  }
  public async loadPhotoshots(url: string) {
    const offset = this.getOffset();
    const res = await axios.get(url, { withCredentials: true });
    if (this.viewType != "simple") {
      return;
    }
    const data = res.data;

    data.forEach((item: RtkFixData, index: number) => {
      const shot = new Photoshot(index, item.imageName);
      const matrix = Utils.rtkFixMatrixToThree(item.transformMatrix, offset);
      shot.applyMatrix4(matrix);
      // @ts-ignore
      shot.accuracy = item.accuracy;

      this.photoshotManager.addPhotoshot(shot);
    });
  }

  setPhotoshotVisible(visible: boolean) {
    this.photoshotManager.setVisible(visible);
  }

  public addPickPoint(id: string, name: string, position: Vector3) {
    this._pointPicker.addPickPoint(id, name, position);
    // this._objectManager.addPickPoint(id, name, position);
    // this._objectManager.animateText(this._cameraManager.camera);
    // const sleep = (second: number) =>
    //   new Promise((resolve) => setTimeout(resolve, second * 1000));
    // await sleep(0.5);
    // this._flgRender = true;
  }

  public updatePickPoint(id: string, name: string, position: Vector3): void {
    this._pointPicker.updatePickPoint(id, name, position);
    // this._objectManager.updatePickPoint(id, name, position);
    // this._flgRender = true;
  }

  public deletePickPoint(id: string): void {
    this._pointPicker.deletePickPoint(id);
    // this._objectManager.deletePickPoint(id);
    // this._flgRender = true;
  }
  public deleteAllPickPoint(): void {
    this._pointPicker.deleteAllPickPoint();
    // this._objectManager.deleteAllPickPoint();
    // this._flgRender = true;
  }

  public startPicking(callbacks: IPointPickerCallback) {
    if (this._inputHandler.getInputHandler().has("pickingMode")) {
      return;
    }
    this._pointPicker?.startPicking(callbacks);
  }

  public endPicking() {
    this._pointPicker?.endPicking();
  }

  public startCoordTracking(callback: (res: Vector3) => void) {
    if (this._inputHandler?.getInputHandler().has("coordTracking")) {
      return;
    }

    this._inputHandler?.registerHandler(
      `coordTracking`,
      new CoordTracker(this, this._inputHandler, callback)
    );
  }

  public endCoordTracking() {
    this._inputHandler?.unregisterHandler(`coordTracking`);
  }

  // onMove (プレースホルダー配置時の相対座標通知) を保持し、placeholder 表示時に
  // 初期座標を通知できるようにする。
  private _designOnMove: ((pos: Vector3) => void) | null = null;

  /**
   * IFC 配置の操作ハンドラを登録する (IFC モード中ずっと有効)。
   * プレースホルダーだけでなく、配置済み (DB) オブジェクトのクリック選択 /
   * ドラッグ移動も扱う。
   * - onMove: プレースホルダーのモデル中心からの相対座標を通知する。
   * - onSelectCommitted: 配置済みオブジェクトを選択 / 解除したとき id (or null)
   *   を通知する (リストのハイライト同期用)。
   */
  public enableDesignObjectInteraction(
    onMove: (pos: Vector3) => void,
    _onSelectCommitted: (id: string | null) => void,
  ) {
    // Click-to-select is now a kho system (editor/system/selectionSystem), which
    // writes $selection and dispatches the "ifc-object-selected" DOM event the
    // drawer listens to. Only the placeholder-position callback stays here.
    this._designOnMove = onMove;
  }

  public disableDesignObjectInteraction() {
    this.getInstanceStore().setSelection(null);
    this.hideDesignPlaceholder();
    this.designDragActive = false;
    this._designOnMove = null;
  }

  /**
   * 配置する新規オブジェクトのプレースホルダーを表示する。プレースホルダーは
   * 編集ストア上の「選択中の暫定インスタンス」そのもの (半透明ゴーストではない)。
   */
  public showDesignPlaceholder(url?: string | null, atWorld?: Vector3 | null) {
    // Drop any previous placeholder before creating a new one.
    this.hideDesignPlaceholder();
    if (url) {
      const p = atWorld ?? new Vector3(0, 0, 0);
      this._placeholderInstanceId = this.placeInstanceAt(url, p, null);
      // No valid drop point yet → hide until moveDesignPlaceholderToWorld shows it.
      if (!atWorld) this.setDesignPlaceholderVisible(false);
    }
    // 初期位置 (中心) を通知
    this._designOnMove?.(new Vector3(0, 0, 0));
  }

  public hideDesignPlaceholder() {
    const id = this._placeholderInstanceId;
    if (id) {
      // Only a non-committed placeholder is disposable; a saved object stays.
      const inst = this.getInstanceStore().getInstances().find((i) => i.id === id);
      if (inst && !inst.committedId) this.getInstanceStore().remove(id);
      this._placeholderInstanceId = null;
    }
  }

  // Place a data-driven instance (POC model) at a surface point + normal.
  // Returns the new instance id (so the caller can attach a committedId post-POST).
  public placeInstanceAt(
    url: string,
    point: Vector3,
    normal?: Vector3 | null,
  ): string | null {
    const store = this.getInstanceStore();
    const up =
      normal && normal.lengthSq() > 1e-6
        ? normal.clone().normalize()
        : new Vector3(0, 0, 1);
    const q = new Quaternion().setFromUnitVectors(new Vector3(0, 0, 1), up);
    store.place(
      url,
      [point.x, point.y, point.z],
      [q.x, q.y, q.z, q.w],
      [up.x, up.y, up.z],
    );
    return store.getSelection();
  }

  public setInstanceCommitted(instanceId: string, committedId: string) {
    this.getInstanceStore().setCommittedId(instanceId, committedId);
    this._committedToInstance.set(committedId, instanceId);
    // Once the placeholder is persisted it becomes a real committed object;
    // stop tracking it as a disposable placeholder.
    if (this._placeholderInstanceId === instanceId) {
      this._placeholderInstanceId = null;
    }
  }

  /**
   * ドラッグ&ドロップ配置用: 正規化デバイス座標 (NDC, -1..1) のレイを点群へ当てて
   * ドロップ地点のワールド座標を返す。点群に当たらなければ null。
   */
  public getDropSurfacePoint(
    ndcX: number,
    ndcY: number,
    refine = false,
  ): Vector3 | null {
    const hit = this.getInstanceStore().surfacePick(ndcX, ndcY, refine);
    return hit ? new Vector3().fromArray(hit.position) : null;
  }

  // ===== point-cloud surface sampling ======================================
  // The screen-ray pick against the point cloud (so dropped objects stick to the
  // CLOUD surface — spec: 点群に擦りつく) now lives in the kho surfaceSystem,
  // published via $surfacePick and reached through the instanceStore facade
  // (surfacePick). The Viewer still owns _gatherCloudScenes because the store's
  // ViewerHost.getCloudScenes bridge reads it.

  private _gatherCloudScenes(): Object3D[] {
    const scenes: Object3D[] = [];
    for (const t of [
      SCENE_TYPE.STREAM_POINTCLOUD,
      SCENE_TYPE.STATIC_POINTCLOUD,
    ]) {
      const s = this.getScene(t);
      if (s) scenes.push(s);
    }
    return scenes;
  }

  /** ドラッグ配置プレビュー: プレースホルダー (選択中の暫定インスタンス) を移動。 */
  public moveDesignPlaceholderToWorld(pos: Vector3) {
    const id = this._placeholderInstanceId;
    if (!id) return;
    this.getInstanceStore().transform({
      id,
      position: [pos.x, pos.y, pos.z],
    });
    // A valid surface point arrived → make sure it is visible.
    this.setDesignPlaceholderVisible(true);
    this.updateRenderer();
  }

  /** プレースホルダーの表示/非表示 (点群にヒットしない間は隠す)。 */
  public setDesignPlaceholderVisible(v: boolean) {
    const mesh = this._instanceMeshById(this._placeholderInstanceId);
    if (mesh) {
      mesh.visible = v;
      this.updateRenderer();
    }
  }

  /** ドロップ地点の点群サーフェス (位置 + 法線)。refine=true で GPU サンプル。 */
  public getDropSurface(
    ndcX: number,
    ndcY: number,
    refine = true,
  ): { point: Vector3; normal: Vector3 } | null {
    const hit = this.getInstanceStore().surfacePick(ndcX, ndcY, refine);
    return hit
      ? {
          point: new Vector3().fromArray(hit.position),
          normal: new Vector3().fromArray(hit.normal),
        }
      : null;
  }

  /** 現在の対象 (選択中インスタンス) を点群法線に沿って傾ける。 */
  public tiltDesignPlaceholder(normal: Vector3) {
    const store = this.getInstanceStore();
    const inst = store.getSelected();
    if (!inst || normal.lengthSq() < 1e-6) return;
    const n = normal.clone().normalize();
    if (n.z < 0) n.negate();
    const q = new Quaternion().setFromUnitVectors(new Vector3(0, 0, 1), n);
    store.transform({
      id: inst.id,
      up: [n.x, n.y, n.z],
      quaternion: [q.x, q.y, q.z, q.w],
    });
    this.updateRenderer();
  }

  // ===== IFC 配置オブジェクトの選択 / gizmo overlay 用 API =====

  // プレースホルダー (配置中の新規オブジェクト) を選択する。
  public selectDesignObject() {
    if (this._placeholderInstanceId) {
      this.getInstanceStore().setSelection(this._placeholderInstanceId);
    }
  }

  // 配置済み (DB) オブジェクトを committedId で選択する。
  public selectDesignObjectCommitted(id: string) {
    const inst = this.getInstanceStore().getByCommittedId(id);
    if (inst) this.getInstanceStore().setSelection(inst.id);
  }

  public deselectDesignObject() {
    this.getInstanceStore().setSelection(null);
  }

  public isDesignObjectSelected(): boolean {
    return this.getInstanceStore().getSelection() != null;
  }

  // 鉛直 (Z) 軸まわりに配置オブジェクトを回転する (右ドラッグ / 回転ボタン用)。
  // instance の up 軸まわりに quaternion を回し、baseYaw も同じだけ更新する。
  public rotateDesignObjectZ(deltaRad: number) {
    const store = this.getInstanceStore();
    const inst = store.getSelected();
    if (!inst) return;
    const axis = new Vector3().fromArray(inst.up);
    if (axis.lengthSq() < 1e-6) axis.set(0, 0, 1);
    axis.normalize();
    const spin = new Quaternion().setFromAxisAngle(axis, deltaRad);
    const q = spin.multiply(new Quaternion().fromArray(inst.quaternion));
    store.transform({
      id: inst.id,
      quaternion: [q.x, q.y, q.z, q.w],
      baseYaw: inst.baseYaw + deltaRad,
    });
    this.updateRenderer();
  }

  // 配置オブジェクトを等倍スケールする (マウスホイール / +- ボタン用)。
  public scaleDesignObject(factor: number) {
    const store = this.getInstanceStore();
    const inst = store.getSelected();
    if (!inst || factor <= 0) return;
    const next = Math.min(Math.max(inst.scale * factor, 0.05), 20);
    store.transform({ id: inst.id, scale: next });
    this.updateRenderer();
  }

  // 配置済み (DB) オブジェクト 1 件の現在 transform (永続化用)。committedId から
  // instance を引き、quaternion を Euler に変換して返す。
  public getCommittedTransform(id: string) {
    const inst = this.getInstanceStore().getByCommittedId(id);
    if (!inst) return null;
    const e = new Euler().setFromQuaternion(
      new Quaternion().fromArray(inst.quaternion),
    );
    return {
      posX: inst.position[0],
      posY: inst.position[1],
      posZ: inst.position[2],
      rotX: e.x,
      rotY: e.y,
      rotZ: e.z,
      scale: inst.scale,
    };
  }

  // 配置済みオブジェクトを編集したことを React (ドロワー) に通知する。
  // React 側がデバウンスして update API を呼び永続化する。
  public notifyDesignObjectEdited(id: string) {
    document.dispatchEvent(
      new CustomEvent("ifc-object-edited", { detail: { id } }),
    );
  }

  // 配置オブジェクトが Viewer 外へドラッグ&ドロップされたことを React (ドロワー)
  // に通知する。committedId があれば配置済み (DB) → ドロワーが DB + 描画から削除、
  // null ならプレースホルダー (新規・未保存) → ドロワーが選択解除して非表示にする。
  public notifyDesignObjectDroppedOutside(committedId: string | null) {
    document.dispatchEvent(
      new CustomEvent("ifc-object-drop-outside", {
        detail: { id: committedId },
      }),
    );
  }

  /**
   * 配置オブジェクトのワールド AABB をビューポート絶対座標の矩形 (CSS ピクセル,
   * position:fixed 用) に投影して返す。overlay gizmo を枠に追従させるために使う。
   * 未選択 / 未配置なら null。
   */
  public getDesignObjectScreenRect():
    | { x: number; y: number; width: number; height: number }
    | null {
    const mesh = this._selectedInstanceMesh();
    if (!mesh) return null;
    const box = new Box3().setFromObject(mesh);
    if (box.isEmpty()) return null;

    const rect = this.getBoundingRect();
    const camera = this._cameraManager.camera;
    const corners = [
      new Vector3(box.min.x, box.min.y, box.min.z),
      new Vector3(box.min.x, box.min.y, box.max.z),
      new Vector3(box.min.x, box.max.y, box.min.z),
      new Vector3(box.min.x, box.max.y, box.max.z),
      new Vector3(box.max.x, box.min.y, box.min.z),
      new Vector3(box.max.x, box.min.y, box.max.z),
      new Vector3(box.max.x, box.max.y, box.min.z),
      new Vector3(box.max.x, box.max.y, box.max.z),
    ];

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const v = new Vector3();
    for (const c of corners) {
      v.copy(c).project(camera);
      const sx = rect.left + ((v.x + 1) / 2) * rect.width;
      const sy = rect.top + ((1 - v.y) / 2) * rect.height;
      if (sx < minX) minX = sx;
      if (sy < minY) minY = sy;
      if (sx > maxX) maxX = sx;
      if (sy > maxY) maxY = sy;
    }

    if (!isFinite(minX) || !isFinite(minY)) return null;
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  /**
   * 配置中オブジェクトを指定方向から見る (多方向ビュー)。
   * view: top / bottom / front / back / left / right。
   */
  public setDesignObjectView(view: TDesignObjectView) {
    const mesh = this._selectedInstanceMesh();
    const inst = this.getInstanceStore().getSelected();
    const box = mesh ? new Box3().setFromObject(mesh) : null;
    const center =
      box && !box.isEmpty()
        ? box.getCenter(new Vector3())
        : inst
          ? new Vector3().fromArray(inst.position)
          : new Vector3();
    const size = box && !box.isEmpty() ? box.getSize(new Vector3()) : null;
    const radius = size ? size.length() / 2 : 5;
    const distance = Math.max(radius * 3, 1);

    const dirMap: Record<TDesignObjectView, Vector3> = {
      top: new Vector3(0, 0, 1),
      bottom: new Vector3(0, 0, -1),
      front: new Vector3(0, -1, 0),
      back: new Vector3(0, 1, 0),
      left: new Vector3(-1, 0, 0),
      right: new Vector3(1, 0, 0),
    };
    this._cameraManager.setViewDirection(dirMap[view], center, distance);
  }

  /**
   * ドロップ確定: 追従中プレースホルダーを「配置済み (未保存)」として確定する。
   * インスタンスは編集ストアに残したまま追従ポインタ (_placeholderInstanceId)
   * だけを外すので、次に別オブジェクトをドラッグしても消えない (複数を置いてから
   * まとめて Save できる)。確定したインスタンス id を返す。
   */
  public finalizeDesignPlaceholder(): string | null {
    const id = this._placeholderInstanceId;
    this._placeholderInstanceId = null;
    return id;
  }

  /** 任意インスタンスの現在 transform (保存用)。位置は点群座標系。無ければ null。 */
  public getInstanceTransform(instanceId: string) {
    const inst = this.getInstanceStore()
      .getInstances()
      .find((i) => i.id === instanceId);
    if (!inst) return null;
    const e = new Euler().setFromQuaternion(
      new Quaternion().fromArray(inst.quaternion),
    );
    return {
      position: new Vector3().fromArray(inst.position),
      rotationX: e.x,
      rotationY: e.y,
      rotationZ: e.z,
      scale: inst.scale,
    };
  }

  /** 未保存の配置インスタンスを 1 件消す (Save 後の後始末 / キャンセル用)。 */
  public removePlacementInstance(instanceId: string) {
    this.getInstanceStore().remove(instanceId);
    if (this._placeholderInstanceId === instanceId)
      this._placeholderInstanceId = null;
  }

  /**
   * 保存済み (確定) IFC オブジェクト群を編集ストアへ差分反映 (reconcile) する。
   * committedId をキーに、消えたものは remove、既存は transform、新規は place。
   * 全消し→再構築ではないので再ダウンロード / チラつきを避ける。
   */
  public renderSavedIfcObjects(objects: TSavedIfcObject[]) {
    const store = this.getInstanceStore();
    const incoming = new Set(objects.map((o) => o.id));
    // remove instances whose saved object vanished
    for (const [cid, iid] of Array.from(this._committedToInstance)) {
      if (!incoming.has(cid)) {
        store.remove(iid);
        this._committedToInstance.delete(cid);
      }
    }
    // loading saved objects must not steal the current selection/gizmo
    const keepSelection = store.getSelection();
    for (const obj of objects) {
      if (!obj.url) continue;
      const q = new Quaternion().setFromEuler(
        new Euler(obj.rotX || 0, obj.rotY || 0, obj.rotZ || 0),
      );
      const pos: [number, number, number] = [obj.posX, obj.posY, obj.posZ];
      const scale = obj.scale && obj.scale > 0 ? obj.scale : 1;
      const iid = this._committedToInstance.get(obj.id);
      if (iid) {
        store.transform({
          id: iid,
          position: pos,
          quaternion: [q.x, q.y, q.z, q.w],
          scale,
        });
      } else {
        store.setObjectScale(scale);
        store.place(obj.url, pos, [q.x, q.y, q.z, q.w], [0, 0, 1], obj.id);
        const newId = store.getSelection();
        if (newId) this._committedToInstance.set(obj.id, newId);
      }
    }
    store.setObjectScale(1);
    store.setSelection(keepSelection);
  }

  /** 保存済み IFC オブジェクトの描画をすべて消す。 */
  public clearSavedIfcObjects() {
    const store = this.getInstanceStore();
    for (const iid of this._committedToInstance.values()) store.remove(iid);
    this._committedToInstance.clear();
  }

  /** 指定 committedId の保存済み IFC オブジェクトを 1 件消す。 */
  public removeSavedIfcObject(id: string) {
    const iid = this._committedToInstance.get(id);
    if (!iid) return;
    const store = this.getInstanceStore();
    if (store.getSelection() === iid) store.setSelection(null);
    store.remove(iid);
    this._committedToInstance.delete(id);
  }

  /**
   * 4 方向ボタン: 選択中オブジェクトを画面 XY 方向へ一定量ずらす。
   * 移動量はオブジェクトサイズに比例 (取得できなければ既定値)。
   */
  public nudgeDesignObject(dirX: number, dirY: number) {
    const store = this.getInstanceStore();
    const inst = store.getSelected();
    if (!inst) return;
    const mesh = this._selectedInstanceMesh();
    let step = 0.1;
    if (mesh) {
      const size = new Box3().setFromObject(mesh).getSize(new Vector3());
      step = Math.max(size.length() * 0.05, 0.05);
    }
    const p = inst.position;
    store.transform({
      id: inst.id,
      position: [p[0] + dirX * step, p[1] + dirY * step, p[2]],
    });
    this.updateRenderer();
  }

  public saveController(): void {
    // this._controller.saveState();
  }

  public resetController(): void {
    // this._controller.reset();
  }

  public fit(): void {
    this._cameraManager.resetCurrentCameraTransforms();
    const bbSize = this._pointCloudManager.getSize() || new Vector3(1, 1, 1);
    let bbLength = bbSize.length();
    const center = this._pointCloudManager.getCenter() || new Vector3(0, 0, 0);
    if (!this._pointCloudManager.bvh.rootTile) return;
    if (this._cameraManager.camera instanceof Quick3DOrthographicCamera) {
      const fitOrthographicParameters = {
        bottomPadding: 0,
        leftPadding: 0,
        rightPadding: 0,
        topPadding: 0,
        distanceToPlaceCamera: bbLength,
      } as IOrthographicFitParameters;
      this._cameraManager.camera.fit(
        this._pointCloudManager.bvh.rootTile.boundingBox,
        fitOrthographicParameters
      );
    } else {
      this._cameraManager.camera.fit(
        this._pointCloudManager.bvh.rootTile.boundingBox
      );
    }
  }

  private fitAllCamera() {
    this._cameraManager.resetCurrentCameraTransforms();
    const bbSize = this._pointCloudManager.getSize() || new Vector3(1, 1, 1);
    let bbLength = bbSize.length();
    if (!this._pointCloudManager.bvh.rootTile) return;
    const fitOrthographicParameters = {
      bottomPadding: 0,
      leftPadding: 0,
      rightPadding: 0,
      topPadding: 0,
      distanceToPlaceCamera: bbLength,
    } as IOrthographicFitParameters;
    this._cameraManager.orthographicCamera.fit(
      this._pointCloudManager.bvh.rootTile.boundingBox,
      fitOrthographicParameters
    );
    const helper = this._cameraManager.perspectiveCamera.fit(
      this._pointCloudManager.bvh.rootTile.boundingBox
    );

    // this.getScene(SCENE_TYPE.BACKGROUND).add(helper);
  }

  // public setPickPointPosition(
  //   index: number,
  //   x: number,
  //   y: number,
  //   z: number
  // ): void {
  //   this._objectManager.setPickPoint(index, new Vector3(x, y, z));
  //   // this._flgRender = true;
  //   this.updateRenderer();
  // }

  public RemovePointCloud(): void {
    this._objectManager.removePointCloud();
    // this._flgRender = true;
  }

  // public TogglePointCloud(): void {
  //   this._objectManager.togglePointCloud();
  //   // this._flgRender = true;
  //   this.updateRenderer();
  // }

  public ToggleTrajectory(): void {
    this._objectManager.toggleTrajectory();
    // this._flgRender = true;
  }

  public RemoveTrajectory(): void {
    this._objectManager.removeTrajectory();
    // this._flgRender = true;
  }

  public intersectPointCloud(
    e: { clientX: number; clientY: number },
    refine = false
  ): Vector3 {
    const coords = Utils.CalculateMousePosition(e, this._canvas);
    const pickCoord = {
      x: ((coords.x + 1) * this._canvas.width) / 2,
      y: ((coords.y + 1) * this._canvas.height) / 2,
    };

    const gl: WebGLRenderingContext = this._canvas.getContext("webgl2");

    return this._pointCloudManager.intersectPointCloud(gl, pickCoord, refine);
  }

  public intersectPointCloudFromRay(e: Raycaster) {
    return this._pointCloudManager.intersectPointCloudFromRay(e);
  }

  public getThreshold() {
    const mode = Array.from(this._inputHandler.getInputHandler().keys())[0];
    if (mode !== "pickingMode") return 0.1;

    if (this._cameraManager.camera instanceof OrthographicCamera) {
      const scale =
        ((this._cameraManager.camera.right - this._cameraManager.camera.left) /
          this._cameraManager.camera.zoom) *
        0.002;
      return Math.min(scale, 0.05);
    }
    return 0.05;
  }

  public getPointCloudOffset() {
    return this._objectManager.getPointCloudOffset();
  }

  // public togglePickPointGuide(isShow: boolean): void {
  //   this._objectManager.togglePickPointGuide(isShow);
  //   // this._flgRender = true;
  //   this.updateRenderer();
  // }

  /**
   * DO NOT CALL THIS. It does nothing.
   *
   * It used to bump the old dirty-flag counter (`_renderLoopCount`) so the loop
   * would draw a few more frames. The loop now redraws the whole scene every
   * frame at the capped FPS, so nothing has to ask for a render: just update the
   * data (camera, scene objects, uniforms) and the next frame shows it.
   *
   * Kept `public` on purpose so the existing call sites still compile.
   */
  public updateRenderer(renderCount: number = 2): void {
    this._renderLoopCount = renderCount;
    // this.MainRender()
    // this._flgRender = true;
  }

  // public getPointSize(): number {
  //   return this._pointCloudManager.getPointSize();
  // }

  public deleteAllObject(): void {
    this._objectManager.deleteAllObject();
  }

  public updatePickPointGuide(position: Vector3) {
    this._pointPicker.updatePickPointGuide(position);
  }

  public undoMeasurement() {
    const command = this._measurementCommandHistory.getUndoCommand();
    command && command.execute();
  }

  public redoMeasurement() {
    const command = this._measurementCommandHistory.getRedoCommand();
    command && command.execute();
  }

  public cancelEditingMeasurement() {
    // QU3DW-1379 (SC_QUICK-2063): restoring the pre-edit points is now done by
    // MeasurementManager.cancelEditingMeasurement() from its lastSavedPoints
    // cache — this no longer relies on replaying _measurementCommandHistory,
    // which only ever held a single, never-superseded baseline command and so
    // never actually restored anything.
    this._measurementManager.cancelEditingMeasurement();
    this._measurementCommandHistory.clear();
    this._measurementManager.changeAppearanceByState(
      this._measurementManager.activeMeasurement.id,
      "selected"
    );
  }

  public generateMeasurementId(): number {
    return this._measurementManager.generateMeasurementId();
  }

  public setMeasurementId(id: number) {
    this._measurementManager.setMeasurementId(id);
  }

  public initMeasurementMap() {
    this._measurementManager.initMeasurementMap();
  }

  public async createMeasurement(
    params: TCreateMeasurementParams,
    textScale: ITextScale
  ): Promise<IMeasurementHandler> {
    this.setPointsToMeasurement(undefined, []);
    this.changeAppearanceBySelected(null);
    const measurement = await this._measurementManager.createMeasurement(
      params,
      textScale
    );
    this._measurementCommandHistory.clear();
    return measurement;
  }

  public async createInitialMeasurement(
    params: TCreateMeasurementParams,
    data: IMeasurement,
    textScale: ITextScale
  ): Promise<IMeasurementHandler> {
    this.setPointsToMeasurement(undefined, []);
    const measurement = await this._measurementManager.createInitialMeasurement(
      params,
      data,
      textScale
    );
    this._measurementCommandHistory.clear();
    return measurement;
  }

  public editMeasurement(
    id: number,
    params: IEditMeasurementParams,
    observers: TMeasurementEditObserver[] = []
  ): void {
    const measurement = this._measurementManager.getMeasurementById(id);
    if (measurement) {
      this._measurementManager.editMeasurement(id, params, observers);
      this.setPointsToMeasurement(id, measurement._points);
    }
  }

  public setPointsToMeasurement(id: number | undefined, points: Vector3[]) {
    const setPointCommand = new SetMeasurementPointsCommand(
      this._measurementManager,
      {
        id,
        points: points.map((p) => p.clone()),
      }
    );
    setPointCommand.execute();
    this._measurementCommandHistory.add(setPointCommand);
    // this._measurementManager.setPoints(id, points);
  }

  public deleteMeasurement(ids?: number | number[]): number | number[] {
    return (
      this._measurementManager &&
      this._measurementManager.deleteMeasurement(ids)
    );
  }

  public changeAppearanceBySelected(id: number) {
    return (
      this._measurementManager &&
      this._measurementManager.changeAppearanceByState(id, "selected")
    );
  }

  public cancelMeasurement(): void {
    this._measurementManager?.cancelMeasurement();
  }

  public finishMeasurement() {
    this._measurementManager?.finishMeasurement();
    this._measurementCommandHistory.clear();
  }

  public resetMeasurement() {
    this._measurementManager?.resetMeasurement();
  }

  public unmark() {
    this._measurementManager?.unmark();
  }

  public async updateEditMeasurement(
    project_id: string,
    updateAt?: string,
    setUpdateAt?: (value: string) => void
  ) {
    await this._measurementManager.updateEditMeasurement(
      project_id,
      updateAt,
      setUpdateAt
    );
  }

  public recordSavedMeasurements(
    measurements: Iterable<{ id: number; _points: Vector3[] }>
  ): void {
    this._measurementManager.recordSavedMeasurements(measurements);
  }

  public finishMeasurementCreation() {
    this.finishMeasurement();
  }

  public setMeasurementVisibility(
    id: number,
    visibleParams: IMeasurementVisibleParams
  ) {
    this._measurementManager.setMeasurementVisibility(id, visibleParams);
  }

  public getMeasurementByType(type: MeasurementType): IMeasurement[] {
    return this._measurementManager.getMeasurementByType(type);
  }

  public calculateVolumeInfo(
    polygon: Vector3[],
    meshIndices: number[]
  ): IVolumeInfo {
    return this._pointCloudManager.calculateVolumeInfo(polygon, meshIndices);
  }

  public applyHeightBasedColor(
    polygon: { vertices: Vector3[]; indices: number[] },
    rangeInfo: IPointColorRangeInfo
  ) {
    this._pointCloudManager.applyHeightBasedColor(polygon, rangeInfo);
  }

  public setView(type: setViewType) {
    const center = (this._pointCloudManager.getCenter() ||  new Vector3(0,0,0)).clone();
    
    let bbSize = this._pointCloudManager.getSize();
    
    // If getSize() returns undefined, calculate from designManager mesh
    if (!bbSize && this.designManager.groupBoundingBox) {
      bbSize = this.designManager.groupBoundingBox.getSize(new Vector3());
    }
    
    // Fallback to default size if still not available
    if (!bbSize) {
      bbSize = new Vector3(1, 1, 1);
    }
    
    let bbLength = bbSize.length();
    if (type != setViewType.TOP_SAME_DIRECTION) {
      this.fit();
    }

    const camera = this._cameraManager.camera;

    switch (type) {
      case setViewType.FRONT:
        this._cameraManager.camera.position.set(
          center.x,
          center.y - bbLength * 2,
          center.z
        );
        this._cameraManager.camera.up.set(0, 0, 1);
        break;
      case setViewType.BACK:
        this._cameraManager.camera.position.set(
          center.x,
          center.y + bbLength,
          center.z
        );
        this._cameraManager.camera.up.set(0, 0, 1);
        break;
      case setViewType.RIGHT:
        this._cameraManager.camera.position.set(
          center.x + bbLength,
          center.y,
          center.z
        );
        this._cameraManager.camera.up.set(0, 0, 1);
        break;
      case setViewType.LEFT:
        this._cameraManager.camera.position.set(
          center.x - bbLength,
          center.y,
          center.z
        );
        this._cameraManager.camera.up.set(0, 0, 1);
        break;
      case setViewType.TOP:
        this._cameraManager.camera.position.set(
          center.x,
          center.y,
          center.z + bbLength
        );
        this._cameraManager.camera.up.set(0, 1, 0);
        break;
      case setViewType.BOTTOM:
        this._cameraManager.camera.position.set(
          center.x,
          center.y,
          center.z - bbLength
        );
        this._cameraManager.camera.up.set(0, -1, 0);

        break;

      case setViewType.TOP_SAME_DIRECTION: {
        camera.updateWorldMatrix(true, false);
        const forward = camera.getWorldDirection(new Vector3());

        const down = new Vector3(0, 0, -1);

        const right = down
          .clone()
          .cross(forward)
          .multiplyScalar(Math.sign(camera.up.z));

        const up = camera.up.clone();
        let newUp = up.clone();

        if (right.length()) {
          newUp = right.clone().cross(down).normalize();
        }
        this.fit();
        this._cameraManager.camera.position.set(
          center.x,
          center.y,
          center.z + bbLength
        );

        if (newUp.length() > 0) {
          camera.up.set(newUp.x, newUp.y, newUp.z);
        }
        break;
      }
      default:
        break;
    }

    this._cameraManager.camera.lookAt(center);
  }

  public setHeightMapVisibility(visibility: boolean) {
    this._pointCloudManager.setHeightMapVisibility(visibility);
  }

  /**
   * Enter "move panel" mode: panels of every IPanelDraggable measurement can be
   * dragged until finishPanelDrag() is called. Registers the drag handler into
   * InputHandler under "drag-panel" (same pattern as "edit-measurement").
   * @param id optional — restrict dragging to a single measurement's panel
   */
  public startPanelDrag(id?: number): void {
    this._panelDragObservable.targetId = id;
    this._inputHandler.registerHandler("drag-panel", this._panelDragObservable);
  }

  /** Leave "move panel" mode: panels are no longer draggable. */
  public finishPanelDrag(): void {
    this._panelDragObservable.cancelDrag();
    this._inputHandler.unregisterHandler("drag-panel");
  }

  public get isPanelDragActive(): boolean {
    return this._inputHandler.getInputHandler().has("drag-panel");
  }

  // Panel drag capability is detected via duck-typing (IPanelDraggable)
  private getPanelDraggableById(id: number): IPanelDraggable | undefined {
    const measurement = this._measurementManager.getMeasurementById(
      id
    ) as unknown as IPanelDraggable;
    if (measurement && typeof measurement.hitPanel === "function") {
      return measurement;
    }
    return undefined;
  }

  public resetPanelPosition(id: number) {
    this.getPanelDraggableById(id)?.resetPanelPosition();
  }

  public getPanelPosition(id: number): Vector3 | undefined {
    return this.getPanelDraggableById(id)?.panelPosition;
  }

  public setPanelPosition(id: number, position: Vector3) {
    this.getPanelDraggableById(id)?.setPanelPosition(position);
  }

  public setHeatMapVisibility(id: number, visibility: boolean) {
    const measurement = this._measurementManager.getMeasurementById(
      id
    ) as VolumeCalculationMeasurement;
    if (
      measurement &&
      measurement.type === MeasurementType.VOLUME_CALCULATION
    ) {
      measurement.heatMapVisibility = visibility;
      if (visibility) {
        this.applyHeightBasedColor(
          measurement.polygonMesh,
          measurement.pointColorRangeInfo
        );
      } else {
        this._pointCloudManager.setHeatMapVisibility(visibility);
        const measurements = this._measurementManager.getMeasurementByType(
          MeasurementType.VOLUME_CALCULATION
        );
        for (const m of measurements as VolumeCalculationMeasurement[]) {
          if (m.id !== id) {
            m.heatMapVisibility &&
              this.applyHeightBasedColor(m.polygonMesh, m.pointColorRangeInfo);
          }
        }
      }
    }

  }

  public getOffset(): Vector3 {
    return this._pointCloudManager.bvh.offset;
  }

  public setPointCloudVisibility(visibility: boolean) {
    this._pointCloudManager?.setPointCloudVisibility(visibility);
  }

  public setProjectionMode(mode: TProjectionMode): void {
    this._cameraManager?.setProjectionMode(mode);
    this._axisUI?.setCamera(this._cameraManager.camera);

    this._onCameraChange();
  }

  public isCameraInit(): Boolean {
    return !!this._cameraManager && !!this._axisUI;
  }

  public getProjectionMode(): TProjectionMode {
    return this._cameraManager?.getProjectionMode();
  }

  public setCameraControl(mode: TControlMode): void {
    this._cameraManager.setControlMode(mode, this.getBoundingBox());
  }

  public setAxisVisiblity(visible: boolean) {
    this._axisUI.setVisibile(visible);
  }

  public getBoundingRect(): DOMRect | ClientRect {
    return this._renderer.domElement.getBoundingClientRect();
  }

  public getCenter(): Vector3 {
    return (
      this._pointCloudManager.bvh?.rootTile?.boundingBox?.getCenter(
        new Vector3()
      ) || new Vector3()
    );
  }

  public getBoundingBox(): Box3 {
    return this._pointCloudManager.bvh?.rootTile?.boundingBox || new Box3();
  }

  public getRootLength(): number {
    return this._pointCloudManager?.bvh?.rootTile?.tileBBox
      .getSize(new Vector3())
      .length();
  }

  //canvas selection

  public startCanvasSelection(
    onSuccess: (data: ISelectionData) => void,
    onError: (message: string) => void,
    onPointClick?: (point: number[][], count: number, total?: number) => void
  ) {
    this._currentSelection = new CanvasPolygonSelection(this);
    // const onResolve = (params: ISelectionData) => {
    //   console.log(params);
    //   const planes = [];
    //   for(let i = 0; i< params.cartesianPoints.length; ++i){
    //     planes.push({normal: new Vector3().fromArray(params.normal[i]), position: new Vector3().fromArray(params.cartesianPoints[i])});
    //   }
    //   this.highlightSelectedPointCloudSection({planes}, new Color("red"))
    // };
    // const onReject = () => {};
    this._currentSelection.start(onSuccess, onError, onPointClick);
  }

  public finishCanvasSelection(): ISelectionData[] | undefined {
    if (this._currentSelection) {
      this._currentSelection.finish();
      const data = this._currentSelection.getTriangulatedSelectionData(this);
      if (data && data.length > 0) {
        return data;
      }
    }
  }

  public undoCanvasSelection() {
    if (this._currentSelection) {
      this._currentSelection.undoDrawing();
    }
  }

  public cancelCanvasSelection() {
    if (this._currentSelection) {
      this._currentSelection.resetDrawing();
    }
  }

  public exitCanvasSelection() {
    if (this._currentSelection) {
      // this._pointCloudManager.resetHighlightSelectedPointCloudSection();
      this._pointCloudManager.resetPointCloudColor();
      this._currentSelection?.dispose();
      this._currentSelection = undefined;
    }
  }

  public resetHighlightSelectedPointCloudSection() {
    this._pointCloudManager.resetHighlightSelectedPointCloudSection();
  }

  // public getJSONOfCanvasSelection(): ISelectionData | undefined {
  //   if(this._currentSelection){
  //     const data = this._currentSelection.getSelectionData(this);
  //     return data.cartesianPoints.length > 0 ? data : undefined;
  //   }
  // }

  //points operation

  // public setColorToPointsUnderSelection(selectionPlanes:ISelectionData, color:string | number){
  //   const group = new Group();
  //   this.getScene(SCENE_TYPE.MEASUREMENT).add(group)
  //   // selectionPlanes.forEach(p=>{
  //     // const helper = new PlaneHelper(selectionPlanes[0], 10);
  //     // group.add(helper);
  //   // })

  //     const planes = [];
  //     for(let i = 0; i< selectionPlanes.cartesianPoints.length; ++i){
  //       planes.push({normal: new Vector3().fromArray(selectionPlanes.normal[i]), position: new Vector3().fromArray(selectionPlanes.cartesianPoints[i])});
  //     }
  //   this._pointCloudManager.highlightSelectedPointCloudSection({planes}, new Color(color));
  // }

  // public selectPointsInsideSelection(
  //   selectionData: ISelectionData[],
  //   color: string | number
  // ) {
  //   // const group = new Group();
  //   // this.getScene(SCENE_TYPE.MEASUREMENT).add(group);
  //   // selectionData.forEach(p=>{
  //   // const helper = new PlaneHelper(selectionData[0], 10);
  //   // group.add(helper);
  //   // })
  //   const convexShapes = [];
  //   for (const data of selectionData) {
  //     const planes: any[] = [];
  //     for (let i = 0; i < data.cartesianPoints.length; ++i) {
  //       planes.push({
  //         normal: new Vector3().fromArray(data.normal[i]),
  //         position: new Vector3().fromArray(data.cartesianPoints[i]),
  //       });
  //     }
  //     convexShapes.push({
  //       planes: planes,
  //       isSelectInside: true,
  //     });
  //   }
  //   // this._pointCloudManager.highlightSelectedPointCloudSection(
  //   //   { convexShapes },
  //   //   new Color(color),
  //   //   true
  //   // );
  //   this.updateRenderer(2);
  // }

  // public selectPointsOutsideSelection(
  //   selectionData: ISelectionData[],
  //   color: string | number
  // ) {
  //   const group = new Group();
  //   this.getScene(SCENE_TYPE.MEASUREMENT).add(group);
  //   // selectionData.forEach(p=>{
  //   // const helper = new PlaneHelper(selectionData[0], 10);
  //   // group.add(helper);
  //   // })
  //   const convexShapes = [];
  //   for (const data of selectionData) {
  //     const planes: any[] = [];
  //     for (let i = 0; i < data.cartesianPoints.length; ++i) {
  //       planes.push({
  //         normal: new Vector3().fromArray(data.normal[i]),
  //         position: new Vector3().fromArray(data.cartesianPoints[i]),
  //       });
  //     }
  //     convexShapes.push({
  //       planes: planes,
  //       isSelectInside: false,
  //     });
  //   }
  //   // this._pointCloudManager.highlightSelectedPointCloudSection(
  //   //   { convexShapes },
  //   //   new Color(color),
  //   //   false
  //   // );
  //   this.updateRenderer(2);
  // }

  public applySelectionsToPointCloud(
    selections: {
      triangles: ISelectionData[];
      isDeleteInside: boolean;
      isDeleted: boolean;
    }[],
    color: string | number
  ) {
    // selectionData.forEach(p=>{
    // const helper = new PlaneHelper(selectionData[0], 10);
    // group.add(helper);
    // })
    const convexShapes = [];
    const shapeCounts = [];
    for (const selection of selections) {
      for (const data of selection.triangles) {
        const planes: any[] = [];
        for (let i = 0; i < data.cartesianPoints.length; ++i) {
          planes.push({
            normal: new Vector3().fromArray(data.normal[i]),
            position: new Vector3().fromArray(data.cartesianPoints[i]),
          });
        }
        convexShapes.push({
          planes: planes,
          isSelectInside: selection.isDeleteInside,
          isDeleted: selection.isDeleted,
        });
      }
      shapeCounts.push(selection.triangles.length);
    }
    this._pointCloudManager.highlightSelectedPointCloudSection(
      { convexShapes, shapeCounts },
      new Color(color)
    );
  }

  public removeSelectedPoints(selectionColor: number | string) {
    this._pointCloudManager.removeSelectedPoints(new Color(selectionColor));
  }

  public resetPointsRemovalProcess() {
    //resets the points and canvas to select again
    //resetPointcloud
    //resetDrawing
  }

  public setMaxIterations(value: number) {
    this._pointCloudManager.pointMaterial.maxIterations = value;
  }

  public getMaxIterations() {
    return this._pointCloudManager.pointMaterial.maxIterations;
  }

  public flipXY(yUp: boolean) {
    this._axisUI.flipXY(yUp);
  }

  public async setTileSplit(thresHold: number) {
    await this._pointCloudManager.setTileSplit(thresHold);
    this._onCameraChange();
  }

  public getTileSplit() {
    return this._pointCloudManager.getTileSplit();
  }

  public toggleIsDebug() {
    this._pointCloudManager.toggleIsDebug();
  }

  public toggleIsRotateDebug() {
    this._cameraManager.toggleIsRotateDebug();
  }

  public setBackgroundColor(color: number, alpha?: number) {
    if (this._renderer) {
      this._renderer.setClearColor(color, alpha);
    }
  }
}
